/** Backend-owned navigation grants and presentation metadata for workspace rails. */
import { SUPABASE_URL } from '../utils/supabase/url';
import { getSupabaseClient, getAuthHeaders } from '../utils/supabase/client';

export const WORKSPACE_NAVIGATION_CHANGED_EVENT = 'workspace-navigation:changed';
// workspace_navigation_batch raises these when the caller has no standing in the scope.
const NAVIGATION_DENIED_CODES = new Set(['42501', '22023']);
export type { WorkspaceNavigationItem } from '../types/workspaceNavigation';
import type { WorkspaceNavigationItem } from '../types/workspaceNavigation';
export interface WorkspaceNavigation {
  workspaceId: string;
  userId: string;
  projectId: string | null;
  main: WorkspaceNavigationItem[];
  project: WorkspaceNavigationItem[];
  canManage: boolean;
}
export interface WorkspaceNavigationMemberItem extends WorkspaceNavigationItem {
  surface: 'main' | 'project';
  defaultAllowed: boolean;
  allowed: boolean;
  override: boolean | null;
  editable: boolean;
  editReason?: string;
}
export interface WorkspaceNavigationMemberPermissions { workspaceId: string; userId: string; platformWorkspace?: boolean; platformMember?: boolean; fullNavigationEditor?: boolean; items: WorkspaceNavigationMemberItem[] }
export interface WorkspaceNavigationBatch {
  workspaceId: string; userId: string; main: WorkspaceNavigationItem[]; canManage: boolean;
  projects: Array<{ projectId: string; items: WorkspaceNavigationItem[] }>;
}
export const WorkspaceNavigationService = {
  async batch(workspaceId: string, projectIds: string[]): Promise<WorkspaceNavigationBatch | null> {
    const { data, error } = await getSupabaseClient().rpc('workspace_navigation_batch', {
      p_workspace_id: workspaceId, p_project_ids: [...new Set(projectIds)].slice(0, 500),
    });
    // Rolling deployments retain the established Edge path until the RPC exists.
    if (error?.code === 'PGRST202' || error?.code === '42883') return null;
    if (error) throw Object.assign(new Error(error.message || 'Unable to load navigation'), { denied: NAVIGATION_DENIED_CODES.has(error.code ?? '') });
    const result = data as unknown as Partial<WorkspaceNavigationBatch> | null;
    if (!result || result.workspaceId !== workspaceId || typeof result.userId !== 'string'
      || !Array.isArray(result.main) || !Array.isArray(result.projects)) throw new Error('Invalid navigation response');
    return result as WorkspaceNavigationBatch;
  },
  async memberPermissions(workspaceId: string, userId: string): Promise<WorkspaceNavigationMemberPermissions> {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/workspace-navigation/workspaces/${encodeURIComponent(workspaceId)}/navigation/members/${encodeURIComponent(userId)}`, { headers: await getAuthHeaders() });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Unable to load navigation permissions');
    return data as WorkspaceNavigationMemberPermissions;
  },
  async setMemberPermission(workspaceId: string, userId: string, surface: 'main' | 'project', id: string, allowed: boolean | null): Promise<WorkspaceNavigationMemberPermissions> {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/workspace-navigation/workspaces/${encodeURIComponent(workspaceId)}/navigation/members/${encodeURIComponent(userId)}`, { method: 'PUT', headers: { ...await getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ surface, id, allowed }) });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Unable to save navigation permissions');
    window.dispatchEvent(new Event(WORKSPACE_NAVIGATION_CHANGED_EVENT));
    return data as WorkspaceNavigationMemberPermissions;
  },
  async resolve(workspaceId: string, projectId?: string | null): Promise<WorkspaceNavigation> {
    const batch = await WorkspaceNavigationService.batch(workspaceId, projectId ? [projectId] : []);
    if (batch) return { workspaceId, userId: batch.userId, projectId: projectId || null, main: batch.main,
      project: projectId ? batch.projects.find(project => project.projectId === projectId)?.items || [] : [], canManage: batch.canManage };
    const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    const response = await fetch(`${SUPABASE_URL}/functions/v1/workspace-navigation/workspaces/${encodeURIComponent(workspaceId)}/navigation${query}`, { headers: await getAuthHeaders() });
    const data = await response.json();
    if (!response.ok) throw Object.assign(new Error(data.error || 'Unable to load navigation'), { denied: response.status === 401 || response.status === 403 });
    return data as WorkspaceNavigation;
  },
};
